import React from 'react';
import { motion } from 'framer-motion';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
const LOGO_NAV_URL = 'https://cdn.magicpatterns.com/uploads/8mGMBFzUfDBa2mdC9g2ZRk/AVY_LOGO.png';
const BRAND_COLORS = [{
  name: 'Purple',
  hex: '#9E00E8',
  meaning: 'Wise guidance',
  description: 'Care, empathy and emotional connection for children and parents.',
  shape: 'circle'
}, {
  name: 'Blue',
  hex: '#0062FF',
  meaning: 'Calm focus',
  description: 'A steady, regulated space where minds can concentrate and feel safe.',
  shape: 'square'
}, {
  name: 'Orange',
  hex: '#FC7010',
  meaning: 'Confident courage',
  description: 'The bravery to try, make mistakes, learn and keep going.',
  shape: 'rectangle'
}, {
  name: 'Magenta',
  hex: '#FF00FC',
  meaning: 'Warm support',
  description: 'Experience, perspective and exam craft that keep the journey on track.',
  shape: 'semi-circle'
}, {
  name: 'Green',
  hex: '#47B500',
  meaning: 'Growth and balance',
  description: 'Healthy, sustainable progress – getting ahead without burning out.',
  shape: 'triangle'
}, {
  name: 'Yellow',
  hex: '#FFE600',
  meaning: 'Optimistic progress',
  description: 'Hope, motivation and mental clarity as children move forward.',
  shape: 'circle'
}];
const ShapeRenderer = ({
  shape,
  color
}: {
  shape: string;
  color: string;
}) => {
  const floatAnimation = {
    y: [0, -10, 0],
    rotate: [0, 5, -5, 0],
    transition: {
      duration: 6,
      repeat: Infinity,
      ease: 'easeInOut'
    }
  };
  switch (shape) {
    case 'circle':
      return <motion.div animate={floatAnimation} className="w-full aspect-square rounded-full" style={{
        backgroundColor: color
      }} data-id="element-283" />;
    case 'square':
      return <motion.div animate={floatAnimation} className="w-full aspect-square" style={{
        backgroundColor: color
      }} data-id="element-284" />;
    case 'rectangle':
      return <motion.div animate={floatAnimation} className="w-full aspect-[16/9]" style={{
        backgroundColor: color
      }} data-id="element-285" />;
    case 'semi-circle':
      return <motion.div animate={floatAnimation} className="w-full aspect-[2/1] rounded-t-full" style={{
        backgroundColor: color
      }} data-id="element-286" />;
    case 'triangle':
      return <motion.div animate={floatAnimation} className="w-full aspect-square" style={{
        clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
        backgroundColor: color
      }} data-id="element-287" />;
    default:
      return <motion.div animate={floatAnimation} className="w-full aspect-square rounded-full" style={{
        backgroundColor: color
      }} data-id="element-288" />;
  }
};
export function ColoursPage() {
  return <div className="min-h-screen bg-[#d9e2e8] p-2 md:p-4 font-sans selection:bg-accent selection:text-white" data-id="element-289">
      <div className="main-container max-w-[1600px] mx-auto bg-card rounded-[1.5rem] md:rounded-[2rem] border border-black/5 shadow-sm min-h-[calc(100vh-2rem)] flex flex-col" data-id="element-290">
        <Navigation activePath="/colours" data-id="element-291" />

        <main className="flex-1 px-4 md:px-16 py-8 md:py-12 space-y-12 md:space-y-24" data-id="element-292">
          <section className="max-w-4xl" data-id="element-293">
            <div className="flex items-center gap-4" data-id="element-294">
              <span className="text-[10px] md:text-xs uppercase tracking-[0.2em] font-medium text-muted-foreground" data-id="element-295">
                Visual Language
              </span>
              <span className="text-xs uppercase tracking-[0.2em] font-medium text-muted-foreground/40" data-id="element-296">
                (Draft)
              </span>
            </div>
            <h1 className="mt-4 text-[clamp(2.5rem,8vw,8rem)] font-semibold leading-[0.95] md:leading-[0.9] tracking-[-0.04em] text-ink" data-id="element-297">
              The Palette
              <span className="text-accent inline-block w-[0.15em] h-[0.15em] rounded-full ml-[0.05em] mb-[0.1em]" data-id="element-298" />
            </h1>
            <p className="mt-6 text-base md:text-lg leading-[1.7] text-ink/70 font-medium max-w-2xl" data-id="element-299">
              A vibrant and energetic colour system designed to inspire
              confidence and clarity in the learning journey.
            </p>
          </section>

          <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-12 md:gap-16" data-id="element-300">
            {BRAND_COLORS.map(color => <div key={color.hex} className="flex flex-col items-center text-center space-y-6 md:space-y-8" data-id="element-301">
                <div className="w-24 h-24 sm:w-32 sm:h-32 md:w-48 md:h-48 flex items-center justify-center" data-id="element-302">
                  <ShapeRenderer shape={color.shape} color={color.hex} data-id="element-303" />
                </div>
                <div className="space-y-2 md:space-y-4 max-w-xs" data-id="element-304">
                  <h3 className="text-xl md:text-3xl font-bold tracking-tight text-ink" data-id="element-305">
                    {color.name}
                  </h3>
                  <div className="space-y-1 md:space-y-2" data-id="element-306">
                    <p className="text-base md:text-xl font-bold text-ink" data-id="element-307">
                      {color.meaning}
                    </p>
                    <p className="text-xs md:text-base leading-[1.6] text-ink/60" data-id="element-308">
                      {color.description}
                    </p>
                  </div>
                </div>
              </div>)}
          </section>
        </main>

        <Footer data-id="element-309" />
      </div>
    </div>;
}